import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../redux/store';
import { setMeasurementSystem } from '../redux/userPreferencesSlice';
import { detectRecipeSystem, type MeasurementSystem } from '../utils/unitSystem';
import type { AdjustOptions } from '../utils/adjustRecipeQuantities';
import type { Recipe } from '../types/recipe';

export interface RecipeAdjustment {
  /** Pass straight to adjustQuantity / adjustQuantitiesInText. */
  options: AdjustOptions;
  /** The system currently shown. */
  system: MeasurementSystem;
  /** The system the recipe was written in — the baseline for "changed". */
  recipeSystem: MeasurementSystem;
  ratio: number;
  /** True when the displayed recipe differs from the one the author wrote. */
  isAdjusted: boolean;
  /** True when only the units differ (used to word the chip). */
  systemChanged: boolean;
  /** True when only the servings differ. */
  servingsChanged: boolean;
  setSystem: (system: MeasurementSystem) => void;
  /** Drop back to the recipe's own units. Servings reset is the caller's. */
  resetSystem: () => void;
}

const toNumber = (value: number | string | undefined | null, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  const parsed = parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Resolves the one adjustment state every recipe surface renders through:
 * the servings ratio and the measurement system, plus whether either differs
 * from the recipe as written.
 *
 * The selected system is a persisted user preference, but it starts as `null`
 * meaning "follow the recipe" — so a recipe opens in its own units and nothing
 * carries a "changed" mark until the user actually asks for a change.
 */
export function useRecipeAdjustment(
  recipe: Recipe | null | undefined,
  servings: number | string | null | undefined,
  /**
   * The recipe's own servings. Pass it where the screen already tracks it
   * (recipe detail), so the ratio can never drift from the servings stepper.
   */
  baseServingsOverride?: number | string | null
): RecipeAdjustment {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const stored = useSelector(
    (state: RootState) => state.userPreferences?.measurementSystem ?? null
  );

  // Detection reads the raw ingredient lines, which is where units actually
  // live — extendedIngredients when present, plain strings otherwise.
  const recipeSystem = useMemo<MeasurementSystem>(() => {
    const lines = recipe?.extendedIngredients?.length
      ? recipe.extendedIngredients.map(ing => ing.original || ing.name)
      : (recipe?.ingredients ?? []);
    return detectRecipeSystem(lines);
  }, [recipe?.extendedIngredients, recipe?.ingredients]);

  const system = stored ?? recipeSystem;

  const baseServings = toNumber(baseServingsOverride ?? recipe?.servings, 4);
  const currentServings = toNumber(servings, baseServings);
  const ratio = baseServings > 0 ? currentServings / baseServings : 1;

  const setSystem = useCallback(
    (next: MeasurementSystem) => { dispatch(setMeasurementSystem(next)); },
    [dispatch]
  );
  const resetSystem = useCallback(
    () => { dispatch(setMeasurementSystem(null)); },
    [dispatch]
  );

  const language = i18n.language;

  const options = useMemo<AdjustOptions>(
    () => ({ ratio, system, recipeSystem, language }),
    [ratio, system, recipeSystem, language]
  );

  const systemChanged = system !== recipeSystem;
  const servingsChanged = ratio !== 1;

  return {
    options,
    system,
    recipeSystem,
    ratio,
    isAdjusted: systemChanged || servingsChanged,
    systemChanged,
    servingsChanged,
    setSystem,
    resetSystem,
  };
}
